import _ from "lodash";
import { Area } from "./models/Area";
import { CuttingQuality } from "./models/Enums";
import { Product } from "./models/Product";
import { ProposalProduct } from "./models/ProposalProduct";

export const calculateRemainingMowerCapacity = (mower: Product, mowerQuantity: number, areas: Area[], possibleToMowOnDifferentHours: boolean, proposalProduct?: ProposalProduct): number => {
    if (!areas?.length) return 100;

    let totalCostForAllAreas = calculateTotalCostForMultipleAreas(areas, mower, possibleToMowOnDifferentHours, proposalProduct);
    const mowerCapacity = (mower.maxAreaSizes[CuttingQuality.Regular] ?? 1) * mowerQuantity;
    const reducedMowerCapacity = mowerCapacity / (getAverageCapacityFactorForAreas(areas));

    let remainingMowerCapacity = (reducedMowerCapacity - totalCostForAllAreas) / reducedMowerCapacity;
    const result = Math.round(100 * remainingMowerCapacity);
    
    // Ensure we never return Infinity or -Infinity
    if (!isFinite(result)) {
        return 0;
    }
    
    return result;
}

const getAverageCapacityFactorForAreas = (areas: Area[]) => {
    if (!areas.length) return 1;

    const capacityTotal = areas.reduce((partSum, a) => partSum + (a.complexityPercentage || 0), 0);
    const capacityAverage = capacityTotal / areas.length;
    return 1 + (capacityAverage / 100); // Divide to get capacityFactor (eg 0.75)
}

export const getCostMultipliersForCuttingQuality = (cuttingQuality: CuttingQuality, maxAreaSizes: Record<CuttingQuality, number>)
    : Record<CuttingQuality, number> => {

    const maxAreaRegular = maxAreaSizes[CuttingQuality.Regular] ?? 1;
    const maxAreaPro = maxAreaSizes[CuttingQuality.Pro] ?? 1;
    const maxAreaProSport = maxAreaSizes[CuttingQuality.ProSport] ?? 1;

    const areaSizeToDivideWith = maxAreaSizes[cuttingQuality] ?? 1;

    const regularMultiplier = maxAreaRegular / areaSizeToDivideWith;
    const proMultipler = maxAreaPro / areaSizeToDivideWith;
    const proSportMultipler = maxAreaProSport / areaSizeToDivideWith;

    const multipliers: Record<CuttingQuality, number> = {
        [CuttingQuality.Regular]: regularMultiplier,
        [CuttingQuality.Pro]: proMultipler,
        [CuttingQuality.ProSport]: proSportMultipler
    };

    return multipliers;
}

export const convertAreaCost = (fromCuttingQuality: CuttingQuality,
    toCuttingQuality: CuttingQuality,
    maxAreaSizes: Record<CuttingQuality, number>, areaSize: number)
    : number => {
    const multipliers = getCostMultipliersForCuttingQuality(fromCuttingQuality, maxAreaSizes);
    const multiplier = multipliers[toCuttingQuality] ?? 1;

    return Math.round(areaSize * multiplier);
}

// I8 - I13 i excelfilen. Kollar hur stor andel (0-1) av maskinens maxActiveHours som areans available hours utgör.
export const calculateAreaMowerAvailabilityRatio = (area: Area, mower: Product): number => {
    // Handle case when availableHoursToCutPerDay is 0, null, or undefined
    const availableHours = area.availableHoursToCutPerDay ?? 0;
    if (availableHours <= 0 || !mower.maxActiveHoursPerDay || mower.maxActiveHoursPerDay <= 0) {
        return 0;
    }
    
    let areaMowerAvailabilityRatio = availableHours / mower.maxActiveHoursPerDay;
    
    if(areaMowerAvailabilityRatio > 1) areaMowerAvailabilityRatio = 1;

    return areaMowerAvailabilityRatio;
}

// J8-J13 i excelfilen.
export const calculateAreaAvailabilityCost = (area: Area, areaAvailabilityRatio: number): number => {
    const availableHours = area.availableHoursToCutPerDay ?? 0;
    if (availableHours === 24) return 0;
    
    // Avoid division by zero - if areaAvailabilityRatio is 0 or very small, return 0
    // This can happen when availableHoursToCutPerDay is 0/null/undefined
    if (areaAvailabilityRatio <= 0 || !isFinite(areaAvailabilityRatio)) return 0;

    return (area.size / areaAvailabilityRatio) - area.size;
}

export const calculateTotalAreaCost = (area: Area, areaAvailabilityCost: number): number => {
    return area.size + areaAvailabilityCost;
}

export const calculateTotalCostForMultipleAreas = (areas: Area[], mower: Product, possibleToMowOnDifferentHours: boolean, proposalProduct?: ProposalProduct): number => {
    let totalCostForAllAreas = 0;
    areas.forEach(area => {
        let availabilityCost = area.size;
        
        // if the area is covered by another mower, detract the covered area from the cost
        if (area.coveragePercent > 0) {
            // if we're calculating the cost for areas in respect to a given proposal product, the proposal product's connection sizes should not be extracted from the area cost
            // since the proposal product's mower is the one that needs to "pay" for the areas
            if (proposalProduct) {
                const otherConnectionTotalSizes = getAreaSizeCoveredByOtherProducts(area, proposalProduct);
                availabilityCost = availabilityCost - otherConnectionTotalSizes;
            } else {
                const coveredAreaSize = getCoveredAreaSize(area);
                availabilityCost = availabilityCost - coveredAreaSize;
            }
        }
        if (!possibleToMowOnDifferentHours) {
            const availabilityRatio = calculateAreaMowerAvailabilityRatio(area, mower);
            availabilityCost += calculateAreaAvailabilityCost(area, availabilityRatio);
        }
        
        const totalAreaCost = convertAreaCost(area.cuttingQuality, CuttingQuality.Regular, mower.maxAreaSizes, availabilityCost);
        totalCostForAllAreas += totalAreaCost;
    })

    return totalCostForAllAreas;
}

export const mowerCanHandleAreaSlope = (mower: Product, area: Area): boolean => {
    return area.maxSlope <= mower.maxSlope;
} 

export const mowerCanHandleAreaCuttingQualities = (mower: Product, areas: Area[]): boolean => {
    if (mower.canHandleMultipleQualities) return true;

    var cuttingQualities: CuttingQuality[] = [];
    areas.forEach(area => {
        if (cuttingQualities.indexOf(area.cuttingQuality) < 0) {
            cuttingQualities.push(area.cuttingQuality);
        }
    })

    return cuttingQualities.length === 1;
}

export const calculateAreaCoverage = (selectedMower: Product, selectedMowerQuantity: number, selectedAreas: Area[], proposalProduct?: ProposalProduct, previoulsyConnectedAreas?: Area[]): Record<string, number> => {
    let recalculatedAreaCoverages: Record<string, number> = {};
    
    if (proposalProduct) {
        recalculatedAreaCoverages = calculateAreaCoverageForExistingMower(proposalProduct, selectedMower, selectedMowerQuantity, selectedAreas, previoulsyConnectedAreas);
    } else {
        recalculatedAreaCoverages = calculateAreaCoverageForNewMower(selectedMower, selectedMowerQuantity, selectedAreas);
    }
    
    return recalculatedAreaCoverages;
}

export const calculateAreaCoverageForNewMower = (selectedMower: Product, selectedMowerQuantity: number, selectedAreas: Area[]): Record<string, number> => {
    // prepare a map to save the recalculated coverage values to
    const recalculatedAreaCoverages: Record<string, number> = {};

    // calculate the total sqm that the mower(s) can handle, for regular quality
    let remainingMowerCapacityRegularSqm = selectedMower.maxAreaSizes[CuttingQuality.Regular] * selectedMowerQuantity;

    selectedAreas.forEach((area) => {
        // only continue with the calculations if the area isn't already completely covered
        if (area.coveragePercent < 100) {
            // get the area sqm that is already covered by other mowers
            const alreadyCoveredAreaSize = 0;//getCoveredAreaSize(area);
            const remainingAreaSize = area.size - alreadyCoveredAreaSize;

            // convert the sqm that the mower(s) can handle, to the "cost" of the area's cutting quality.
            const remainingMowerCapacityForAreaCuttingQuality = convertAreaCost(CuttingQuality.Regular, area.cuttingQuality, selectedMower.maxAreaSizes, remainingMowerCapacityRegularSqm);
    
            // Reduce mower capacity corresponding to the complexityPercentage from the area.
            const reducedMowerCapacity = getReducedMowerCapacity(area, remainingMowerCapacityForAreaCuttingQuality);

            // calculate the ratio between the converted mower capacity and the remaining area size.
            let mowerAreaCoverageRatio = reducedMowerCapacity / remainingAreaSize;
            // never allow more than 100% coverage (= ratio 1)
            if (mowerAreaCoverageRatio > 1) mowerAreaCoverageRatio = 1; 
            // convert to percent
            const mowerAreaCoveragePercent = Math.round(100 * mowerAreaCoverageRatio);
            // add the calculated value to the existing coverage percent (never allow more than 100% coverage)
            let totalAreaCoveragePercent = area.coveragePercent + mowerAreaCoveragePercent;
            if (totalAreaCoveragePercent > 100) totalAreaCoveragePercent = 100;
            // ...and save the result
            recalculatedAreaCoverages[area.id] = totalAreaCoveragePercent;
    
            // calculate how much capacity is left for the mower, when the area has been covered
            const areaCoverageSqm = remainingAreaSize * mowerAreaCoverageRatio;
            const remainingMowerCapacitySqm = reducedMowerCapacity - areaCoverageSqm;
            
            // convert the capacity back to Regular cutting quality...
            const convertedToRegularQuality = convertAreaCost(area.cuttingQuality, CuttingQuality.Regular, selectedMower.maxAreaSizes, remainingMowerCapacitySqm);
            // ...and save it for the next iteration
            remainingMowerCapacityRegularSqm = convertedToRegularQuality;
        }
    })

    return recalculatedAreaCoverages;
}

// selectedAreas = areas that are selected on the client side
// previouslyConnectedAreas = the areas that were connected to the proposal product before the client side editing began
export const calculateAreaCoverageForExistingMower = (proposalProduct: ProposalProduct, selectedMower: Product, selectedMowerQuantity: number, selectedAreas: Area[], previoulsyConnectedAreas?: Area[]): Record<string, number> => {
    // prepare a map to save the recalculated coverage values to
    const recalculatedAreaCoverages: Record<string, number> = {};

    // calculate the total sqm that the mower(s) can handle, for regular quality
    let remainingMowerCapacityRegularSqm = selectedMower.maxAreaSizes[CuttingQuality.Regular] * selectedMowerQuantity;

    selectedAreas.forEach((area) => {
        // check if the area is already covered by other mowers. Calculate how many sqm are left to cover.
        const alreadyCoveredAreaSize = getAreaSizeCoveredByOtherProducts(area, proposalProduct);
        
        const remainingAreaSize = area.size - alreadyCoveredAreaSize;

        // Only continue with the calculations if the area isn't already 100% covered
        if (remainingAreaSize > 0) {
            // convert the sqm that the mower(s) can handle, to the "cost" of the area's cutting quality.
            const remainingMowerCapacityForAreaCuttingQuality = convertAreaCost(CuttingQuality.Regular, area.cuttingQuality, selectedMower.maxAreaSizes, remainingMowerCapacityRegularSqm);
    
            // Reduce mower capacity corresponding to the complexityPercentage from the area.
            const reducedMowerCapacity = getReducedMowerCapacity(area, remainingMowerCapacityForAreaCuttingQuality);

            // calculate the ratio between the converted mower capacity and the remaining area size. Generates a value between 0-1 (where 1 = 100%)
            let areaCoverageRatio = reducedMowerCapacity / remainingAreaSize;
            // never allow more than 100% coverage
            if (areaCoverageRatio > 1) areaCoverageRatio = 1;
            // convert to percent
            const areaCoveragePercent = Math.round(100 * areaCoverageRatio);
            // save the calculated value
            recalculatedAreaCoverages[area.id] = areaCoveragePercent;

            // calculate how much capacity is left for the mower, when the area has been covered
            const areaCoverageSqm = remainingAreaSize * areaCoverageRatio;
            const remainingMowerCapacitySqm = reducedMowerCapacity - areaCoverageSqm;

            // convert the capacity back to Regular cutting quality...
            const convertedToRegularQuality = convertAreaCost(area.cuttingQuality, CuttingQuality.Regular, selectedMower.maxAreaSizes, remainingMowerCapacitySqm);
            // ...and save it for the next iteration
            remainingMowerCapacityRegularSqm = convertedToRegularQuality;
        }
    })

    // if there are any pre-existing area->mower connections, check if these areas are still selected. If any areas have been deselected, recalculate their coverage.
    if (previoulsyConnectedAreas) {
        const deselectedAreas = previoulsyConnectedAreas.filter(a1 => !selectedAreas.find(a2 => a2.id === a1.id));
        if (deselectedAreas) {
            deselectedAreas.forEach(area => {
                const remainingCoveredSize = getAreaSizeCoveredByOtherProducts(area, proposalProduct);
                
                let areaCoverageRatio = remainingCoveredSize / area.size;
                // never allow more than 100% coverage
                if (areaCoverageRatio > 1) areaCoverageRatio = 1;
                // convert to percent
                const areaCoveragePercent = Math.round(100 * areaCoverageRatio);
                // save the calculated value
                recalculatedAreaCoverages[area.id] = areaCoveragePercent;
            })
        }
    }

    return recalculatedAreaCoverages;
}

const getCoveredAreaSize = (area: Area) => {
    const areaSize = area.proposalProductConnections?.reduce((partSum, a) => partSum + a.areaSizeCoveredByProduct, 0);

    return areaSize;
}

export const getAreaSizeCoveredByOtherProducts = (area: Area, proposalProduct: ProposalProduct): number => {
    const otherConnectionsForArea = area.proposalProductConnections?.filter(conn => conn.proposalProduct !== null && conn.proposalProduct.id !== proposalProduct.id);
    const areaSize = otherConnectionsForArea?.reduce((partSum, a) => partSum + a.areaSizeCoveredByProduct, 0);

    return areaSize;
}

function getReducedMowerCapacity(area: Area, remainingMowerCapacityForAreaCuttingQuality: number) {
    const areaComplexityFactor = 1 + (area.complexityPercentage ?? 0) / 100;
    const reducedMowerCapacity = remainingMowerCapacityForAreaCuttingQuality / areaComplexityFactor;
    return reducedMowerCapacity;
}
